import { Diamond, Search } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { toast } from "@/stores/ui";
import { ThemeToggle } from "@/components/shell/ThemeToggle";

const ROUTE_TITLE: Record<string, string> = {
  "/free": "自由创作",
  "/styles": "风格广场",
  "/works": "我的作品",
  "/assets": "素材库",
  "/jobs": "任务中心",
  "/billing": "点数流水",
};

export function Topbar() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const session = useAuthStore((s) => s.session);
  const clear = useAuthStore((s) => s.clear);

  const title = ROUTE_TITLE[location.pathname] ?? "工作台";

  const handleLogout = async () => {
    try {
      await apiFetch("/api/v2/auth/logout", {
        method: "POST",
        body: { refresh_token: session?.refresh_token },
      });
    } catch {
      /* server may have already invalidated */
    }
    clear();
    toast("已退出登录");
  };

  return (
    <header className="glass flex h-16 items-center justify-between gap-5 border-b border-border px-7 max-md:px-4">
      <div className="flex min-w-0 flex-col">
        <h2 className="text-base font-semibold tracking-tight text-foreground">
          {title}
        </h2>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-md:hidden">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
          <Input
            type="search"
            placeholder="搜索素材或任务"
            className="h-9 w-56 rounded-md pl-8 text-sm"
          />
        </div>

        <ThemeToggle compact />

        <div className="inline-flex h-9 items-center gap-2 rounded-full border border-border bg-input px-3 text-sm">
          <Diamond className="h-3 w-3 text-accent" />
          <strong className="font-semibold text-foreground">
            {user?.credit_balance ?? 0}
          </strong>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex h-9 items-center gap-2 rounded-full border border-border bg-transparent px-3 text-sm text-foreground/85 transition-colors hover:bg-white/5"
            >
              <span
                className="h-4 w-4 rounded-full"
                style={{
                  background:
                    "linear-gradient(135deg, hsl(var(--accent)), hsl(8 70% 45%))",
                }}
              />
              <span>{user?.username ?? "未登录"}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold">个人中心</h3>
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <Row k="账号" v={user?.phone ?? "—"} />
              <Row k="用户名" v={user?.username ?? "—"} />
              <Row k="角色" v={user?.role ?? "—"} />
              <Row k="余额" v={`${user?.credit_balance ?? 0} 点`} />
            </div>
            <Button
              variant="ghost"
              className="mt-4 w-full"
              onClick={handleLogout}
            >
              退出登录
            </Button>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}

function Row({ k, v }: { k: string; v: string | number }) {
  return (
    <div className="flex items-baseline justify-between border-b border-border py-1.5 last:border-b-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <strong className="font-medium text-foreground">{v}</strong>
    </div>
  );
}
